import { useMemo, useState } from 'react';
import { createConversationStore } from './state/conversationStore';
import type { ModelId } from './providers/types';
import { useVadSession } from './hooks/useVadSession';
import { useStreamingChat } from './hooks/useStreamingChat';
import { RecordingButton } from './components/RecordingButton';
import { ModelSelector } from './components/ModelSelector';
import { ConversationView } from './components/ConversationView';
import { StatusBar } from './components/StatusBar';
import { HistoryActions } from './components/HistoryActions';

export function App() {
  const store = useMemo(
    () => createConversationStore({ storage: window.localStorage, key: 'phase4a:history:v1' }),
    [],
  );
  const state = store();
  const [model, setModel] = useState<ModelId>('gemini-2.5-flash');
  const chat = useStreamingChat({ store });

  const vad = useVadSession({
    onSegment: async (b64, durationSec) => {
      store.getState().setStatus('inferring');
      await chat.sendSegment(b64, durationSec, model);
    },
  });

  const onStart = async () => {
    await vad.start();
    store.getState().setStatus('listening');
  };
  const onStop = async () => {
    await vad.stop();
    store.getState().setStatus('idle');
  };

  return (
    <main style={{ padding: 24, fontFamily: 'system-ui, sans-serif', maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>音声で AI と話せるデモ（Phase 4a）</h1>
      <p style={{ color: '#555', marginTop: 0 }}>
        🎙 マイクに話しかけると、Gemini / GPT-4o があなたの音声をそのまま聞いて返事します。
        無音になると自動で送信されるので、ボタン操作なしで連続して会話できます。
      </p>

      <section style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '16px 0', flexWrap: 'wrap' }}>
        <RecordingButton
          status={state.status}
          ready={vad.ready}
          recording={vad.recording}
          inferring={state.status === 'inferring'}
          onStart={onStart}
          onStop={onStop}
        />
        <ModelSelector value={model} disabled={state.status === 'inferring'} onChange={setModel} />
        <HistoryActions
          state={state}
          onReset={() => store.getState().reset()}
          disabled={state.status === 'inferring'}
        />
      </section>

      <ConversationView turns={state.turns} userEvents={state.userEvents} />
      <div style={{ marginTop: 12 }}>
        <StatusBar turns={state.turns} />
      </div>
    </main>
  );
}
